// src/components/admin/AdminLogBoard.tsx
import React, { useEffect, useState, useMemo } from 'react';
import { 
  History, X, Calendar, ChevronDown, RefreshCw, LayoutList, AlertCircle,
  TrendingUp, Activity, DollarSign, BarChart3, Target
} from 'lucide-react';
import { crmDataService } from '../../services/crmDataService';

// const API_BASE_URL = 'http://localhost:8000/api';
// API 통합관리
import { API_BASE_URL } from '../../config';

// =============================================================================
// 1. [설정] DB 컬럼명 한글 변환기
// =============================================================================
const FIELD_NAME_MAP: Record<string, string> = {
  'planned_product': '제품금액',
  'planned_item': '상품금액',
  'planned_total': '소계',
  'planned_date': '매출예정일',
  'po_no': 'PO 번호',
  'proj_no': 'Proj 번호',
  'account_name': '거래처명',
  'manager_name': '담당자',
  'area': '지역',
  'grade': '등급',
  'title': '건명',
  'industry': '산업분야',
  'division': '구분',
  'contract_category': '구분',
  'editor_name': '수정자',
  'change_reason': '변경사유',
  'target_table': '대상 테이블',
  'target_id': '대상 ID',
  'changed_at': '변경시간',
  'old_value': '변경 전',
  'new_value': '변경 후',
  'assignee' : '담당자'
  
};

const getKoreanFieldName = (engName: string) => FIELD_NAME_MAP[engName] || engName;

// =============================================================================
// 2. 타입(Interface) 정의
// =============================================================================
interface LogItem {
  id: number;
  target_table: string;
  target_id: string; 
  editor_name: string;
  column_name: string;
  old_value: string;
  new_value: string;
  change_reason: string;
  changed_at: string;
}

interface ForecastItem {
  id: string;
  구분: string;
  poNo: string;
  projNo: string;
  거래처: string;
  담당자: string;
  지역: string;
  제품금액: number;
  상품금액: number;
  소계: number;
  매출예정일: string;
  등급: string;
  건명: string;
  산업분야: string;
  팀: string;
}

interface AdminLogBoardProps {
  globalRefreshKey?: number; // 🚨 [신규 추가] 실시간 신호탄 통로
}

const AdminLogBoard: React.FC<AdminLogBoardProps> = ({ globalRefreshKey = 0 }) => {
  // =============================================================================
  // 3. 상태 관리 (State)
  // =============================================================================
  const [forecastList, setForecastList] = useState<ForecastItem[]>([]);
  const [recentLogs, setRecentLogs] = useState<LogItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());

  const [annualTarget, setAnnualTarget] = useState<number>(0);
  const [dashboardKpi, setDashboardKpi] = useState({
    annual_target: 0,
    total_forecast: 0,
    confirmed_revenue: 0,
    achievement_rate: 0
  });

  // 필터 관련 상태
  const [filterType, setFilterType] = useState<'all' | 'team' | 'individual'>('all');
  const [filterValue, setFilterValue] = useState<string>('');
  const currentYear = new Date().getFullYear();
  const [startDate, setStartDate] = useState<string>(`${currentYear}-01-01`);
  const [endDate, setEndDate] = useState<string>(`${currentYear}-12-31`);

  // 상세 이력 모달 관련
  const [selectedHistory, setSelectedHistory] = useState<LogItem[] | null>(null);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // =============================================================================
  // 4. 데이터 로딩 및 유틸리티 로직 (Logic)
  // =============================================================================
  // 전체 데이터 로드
  useEffect(() => {
    const loadAllData = async () => {
      setIsLoading(true);
      try {
        const [forecastData, logData] = await Promise.all([
          crmDataService.getSalesForecastList(),
          fetch(`${API_BASE_URL}/admin/logs/recent?limit=100`).then(res => res.json())
        ]);
        
        // 🚨 [안전장치] 데이터가 배열이 아닐 경우 빈 배열로 처리하여 화면 깨짐 방지
        setForecastList(Array.isArray(forecastData) ? forecastData : []);
        setRecentLogs(Array.isArray(logData) ? logData : []);
        
      } catch (error) {
        console.error("데이터 로딩 실패:", error);
        setForecastList([]); // 에러 시 빈 배열 초기화
        setRecentLogs([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadAllData();
  }, [reloadKey, globalRefreshKey]); // 🚨 [변경] globalRefreshKey 추가로 외부에서 강제 새로고침 가능하도록

  // 연간 목표 데이터 로드
  useEffect(() => {
    let mounted = true;
    crmDataService.getDashboardKPI(startDate, endDate)
      .then((data: any) => {
        if (!mounted) return;
        const total = Number(data?.annual_target) || 0;
        setAnnualTarget(total);
        setDashboardKpi({
          annual_target: total,
          total_forecast: Number(data?.total_forecast) || 0,
          confirmed_revenue: Number(data?.confirmed_revenue) || 0,
          achievement_rate: Number(data?.achievement_rate) || 0
        });
      })
      .catch(() => {
        if (mounted) {
          setAnnualTarget(0);
          setDashboardKpi({
            annual_target: 0,
            total_forecast: 0,
            confirmed_revenue: 0,
            achievement_rate: 0
          });
        }
      });
    return () => { mounted = false; };
  }, [startDate, endDate]);


  const changedItemIds = useMemo(() => new Set(recentLogs.map(log => String(log.target_id))), [recentLogs]);

  const filteredList = useMemo(() => {
    return forecastList.filter(item => {
      const inDate = item.매출예정일 >= startDate && item.매출예정일 <= endDate;
      const inFilter = filterType === 'all' ||
                       (filterType === 'team' && item.팀 === filterValue) ||
                       (filterType === 'individual' && item.담당자 === filterValue);
      return inDate && inFilter;
    }).sort((a, b) => new Date(a.매출예정일).getTime() - new Date(b.매출예정일).getTime());
  }, [forecastList, filterType, filterValue, startDate, endDate]);

  const filterOptions = useMemo(() => {
    if (filterType === 'team') return Array.from(new Set(forecastList.map(item => item.팀))).filter(Boolean);
    if (filterType === 'individual') return Array.from(new Set(forecastList.map(item => item.담당자))).filter(Boolean);
    return [];
  }, [filterType, forecastList]);

  useEffect(() => { setFilterValue(filterOptions[0] || ''); }, [filterType, filterOptions]);

  const formatKRW = (val: number | string) => {
    const num = typeof val === 'string' ? parseFloat(val.replace(/,/g, '')) : val;
    // 안전하게 변환
    if (isNaN(num as number) || num === null || num === undefined) return "0";
    return new Intl.NumberFormat('ko-KR').format(Math.round(num as number));
  };

  // [연산] KPI 요약 카드 수치 계산
  const kpiSummary = useMemo(() => {
    const totalAmount = dashboardKpi.total_forecast;
    const confirmedAmount = dashboardKpi.confirmed_revenue;
    const currentRate = dashboardKpi.achievement_rate;
    const expectedRate = annualTarget > 0 ? (totalAmount / annualTarget) * 100 : 0;
    return { totalAmount, confirmedAmount, currentRate, expectedRate };
  }, [dashboardKpi, annualTarget]);


  // [함수] 상세 이력 팝업 내 금액 차이 계산
  const getDiffInfo = (colName: string, oldVal: string, newVal: string) => {
    if (colName.includes('product') || colName.includes('total') || colName.includes('item') || colName.includes('금액') || colName.includes('소계')) {
      const oVal = parseInt(String(oldVal).replace(/[^0-9-]/g, '') || '0', 10);
      const nVal = parseInt(String(newVal).replace(/[^0-9-]/g, '') || '0', 10);
      const diff = nVal - oVal;

      if (diff === 0) return null;
      if (diff > 0) return { text: `▲ ${formatKRW(diff)}`, color: 'text-red-600 bg-red-50 border-red-200' };
      if (diff < 0) return { text: `▼ ${formatKRW(Math.abs(diff))}`, color: 'text-blue-600 bg-blue-50 border-blue-200' };
    }
    return null;
  };

  const handleShowHistory = async (id: string) => {
    setCheckedIds(prev => new Set(prev).add(String(id)));
    setIsHistoryLoading(true);
    setSelectedHistory([]);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/logs/${id}`);
      const data = await res.json();
      setSelectedHistory(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("이력 로딩 실패:", error);
    } finally {
      setIsHistoryLoading(false);
    }
  };

  // 🚨 [핵심] 제품/상품금액 개별 변동 화살표 로직
  const getFieldTrend = (itemId: string, fieldType: 'product' | 'item') => {
    const columnName = fieldType === 'product' ? 'planned_product' : 'planned_item';
    const log = recentLogs.find(l => String(l.target_id) === String(itemId) && l.column_name === columnName);
    
    if (!log) return null;
    
    const oldV = parseInt(String(log.old_value).replace(/[^0-9-]/g, '') || '0', 10);
    const newV = parseInt(String(log.new_value).replace(/[^0-9-]/g, '') || '0', 10);
    const diff = newV - oldV;
    
    if (diff > 0) return { icon: '▲', color: 'text-red-500' };
    if (diff < 0) return { icon: '▼', color: 'text-blue-500' };
    return null;
  };

  // =============================================================================
  // 5. 화면 렌더링 (UI)
  // =============================================================================
  return (
    <div className="flex flex-col gap-4 h-full overflow-hidden pb-4">
      
      {/* 🟢 [Row 1] KPI 요약 카드 */}
      <div className="grid grid-cols-4 gap-4 flex-none">
        {/* 카드 1: 연간 목표 */}
        <div className="bg-slate-900 text-white p-4 rounded-2xl shadow-md flex flex-col justify-between h-32">
          <div className="flex justify-between items-start">
            <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider block mb-0.5">Total Target</span>
            <div className="p-1.5 bg-slate-800 rounded-lg text-yellow-400"><Target size={10} /></div>
          </div>
          <span className="text-[11px] font-bold text-slate-400">2026 연간 목표</span>
          <div className="flex items-end gap-2">
             <div className="text-2xl font-black tracking-tight text-white truncate">
               {formatKRW(annualTarget)} 
             </div>
          </div>
          <div className="text-[10px] text-slate-400">연간 목표액</div>
        </div>

        {/* 카드 2: 총 매출 예상 */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-200 flex flex-col justify-between h-32">
          <div>
            <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider block mb-0.5">Total Forecast</span>
            <span className="text-[11px] font-bold text-slate-400">연간 총 매출 예상액</span>
          </div>
          <div className="text-2xl font-black text-slate-800 tracking-tight">{formatKRW(kpiSummary.totalAmount)}</div>
          <div className="flex items-center justify-between text-[10px] mt-1 font-bold text-slate-400">
             <span>예상 달성률</span>
             <span className="text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">{kpiSummary.expectedRate.toFixed(1)}%</span>
          </div>
        </div>

        {/* 카드 3: 확정 매출 */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-200 flex flex-col justify-between h-32">
          <div>
            <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider block mb-0.5">Confirmed Sales</span>
            <span className="text-[11px] font-bold text-slate-400">매출 확정 실적 현황</span>
          </div>
          <div className="text-2xl font-black text-emerald-600 tracking-tight">{formatKRW(kpiSummary.confirmedAmount)}</div>
          <div className="flex items-center justify-between text-[10px] mt-1 font-bold text-slate-400">
             <span>목표 달성률</span>
             <span className="text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-100">{kpiSummary.currentRate.toFixed(1)}%</span>
          </div>
        </div>

        {/* 카드 4: 필터 및 상태 */}
        <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200 flex flex-col justify-center gap-2 h-32">
           <div className="flex items-center justify-between text-xs font-bold text-slate-500 font-black">
             <span>필터 설정</span>
             <button onClick={() => setReloadKey((k) => k + 1)} className="p-1 rounded-full hover:bg-slate-200"><RefreshCw size={14} className={isLoading ? 'animate-spin text-blue-600' : ''} /></button>
           </div>
           <div className="flex items-center gap-1 bg-white border border-slate-200 px-2 py-1.5 rounded-xl">
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="bg-transparent text-[10px] font-black outline-none w-full" />
              <span className="text-slate-300">~</span>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="bg-transparent text-[10px] font-black outline-none w-full" />
           </div>
           <select value={filterType} onChange={(e) => setFilterType(e.target.value as any)} className="text-[10px] font-black p-1 rounded border bg-white outline-none">
              <option value="all">전체 현황</option><option value="team">부서별</option><option value="individual">담당자별</option>
           </select>
        </div>
      </div>

      {/* 🟢 [Row 2] 매출 현황 및 실시간 로그 */}
      <div className="flex flex-col flex-1 min-h-0 gap-4">
        
        {/* 상단: 매출 리스트 */}
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden flex flex-col flex-[2]">
          <div className="p-3 border-b border-slate-100 flex justify-between bg-slate-50/50 flex-none">
             <h3 className="font-black text-slate-800 text-sm flex items-center gap-2"><LayoutList size={16} className="text-blue-600"/> 매출 현황 실시간 모니터링</h3>
          </div>
          <div className="overflow-auto flex-1 scrollbar-thin">
            {/* 🚨 table-fixed 및 min-w 설정으로 가로 스크롤 확보 + 디자인 유지 */}
            <table className="w-full text-left border-collapse table-fixed min-w-[1200px]">
              <thead className="sticky top-0 z-20 bg-slate-50 text-slate-500 text-[10px] font-black border-b border-slate-200">
                <tr className="h-10">
                  <th className="px-3 w-[6%] text-center uppercase">구분</th>
                  <th className="px-3 w-[8%] uppercase font-black tracking-tight">PO No.</th>
                  <th className="px-3 w-[8%] uppercase font-black tracking-tight">Proj No.</th>
                  <th className="px-3 w-[12%] uppercase font-black tracking-tight">거래처</th>
                  <th className="px-3 w-[6%] uppercase font-black tracking-tight">담당자</th>
                  <th className="px-3 w-[6%] uppercase font-black tracking-tight">지역</th>
                  <th className="px-3 w-[10%] text-right uppercase font-black tracking-tight">제품금액</th>
                  <th className="px-3 w-[10%] text-right uppercase font-black tracking-tight">상품금액</th>
                  <th className="px-3 w-[12%] text-right uppercase text-indigo-600 font-black tracking-tight">소계(합계)</th>
                  <th className="px-3 w-[8%] text-center uppercase font-black tracking-tight">매출예정일</th>
                  <th className="px-3 w-[4%] text-center uppercase font-black tracking-tight">등급</th>
                  <th className="px-3 w-[18%] uppercase font-black tracking-tight">건명</th>
                  <th className="px-3 w-[6%] text-center uppercase sticky right-0 bg-slate-50 border-l border-slate-200 shadow-[-4px_0_10px_rgba(0,0,0,0.02)]">관리</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs font-bold bg-white">
                {filteredList.map((item) => {
                  const isUnread = changedItemIds.has(String(item.id)) && !checkedIds.has(String(item.id));
                  
                  // 🚨 [핵심] 트렌드(화살표) 데이터 계산
                  const pTrend = getFieldTrend(item.id, 'product');
                  const iTrend = getFieldTrend(item.id, 'item');

                  return (
                    <tr key={item.id} className={`transition-colors h-11 ${isUnread ? 'bg-orange-50/50' : 'hover:bg-slate-50'}`}>
                      <td className="px-3 text-center text-slate-500">{item.구분}</td>
                      <td className="px-3 text-slate-400 font-mono truncate">{item.poNo || '-'}</td>
                      <td className="px-3 text-slate-400 font-mono truncate">{item.projNo || '-'}</td>
                      <td className="px-3 font-black text-slate-800 truncate">{item.거래처}</td>
                      <td className="px-3 text-blue-600">{item.담당자}</td>
                      <td className="px-3 text-slate-500 truncate">{item.지역 || '-'}</td>
                      
                      {/* 🚨 [수정] 제품금액 (금액 + 오른쪽 화살표) */}
                      <td className="px-3 text-right font-mono text-slate-600">
                        <div className="flex items-center justify-end gap-1">
                          {formatKRW(item.제품금액)}
                          {pTrend && <span className={`${pTrend.color} text-[10px] animate-bounce font-black`}>{pTrend.icon}</span>}
                        </div>
                      </td>

                      {/* 🚨 [수정] 상품금액 (금액 + 오른쪽 화살표) */}
                      <td className="px-3 text-right font-mono text-slate-600">
                        <div className="flex items-center justify-end gap-1">
                          {formatKRW(item.상품금액)}
                          {iTrend && <span className={`${iTrend.color} text-[10px] animate-bounce font-black`}>{iTrend.icon}</span>}
                        </div>
                      </td>

                      <td className="px-3 text-right font-mono font-black text-indigo-700 bg-indigo-50/20">{formatKRW(item.소계)}</td>
                      <td className="px-3 text-center font-mono text-slate-500">{item.매출예정일}</td>
                      <td className="px-3 text-center"><span className="px-1.5 py-0.5 rounded text-[9px] bg-slate-100">{item.등급}</span></td>
                      <td className="px-3 text-slate-500 truncate" title={item.건명}>{item.건명}</td>
                      <td className="px-3 text-center sticky right-0 bg-white border-l border-slate-200 shadow-[-4px_0_10px_rgba(0,0,0,0.02)]">
                        <button onClick={() => handleShowHistory(item.id)} className={`p-1.5 rounded-lg ${isUnread ? 'bg-white text-orange-600 border border-orange-200 shadow-sm animate-pulse' : 'text-slate-300 hover:text-slate-600'}`}><History size={14} /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* 하단: 실시간 로그 스트림 */}
        <div className="bg-slate-900 rounded-2xl shadow-xl border border-slate-800 overflow-hidden text-slate-300 flex flex-col flex-[1.2]">
          <div className="p-3 border-b border-slate-800 flex items-center justify-between bg-slate-950 px-5 flex-none">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
              <h3 className="font-black text-[11px] text-white tracking-widest uppercase tracking-tight">실시간 시스템 변경 로그 (Live Stream)</h3>
            </div>
            <div className="text-[10px] text-slate-500 font-mono italic">connected: live_sync</div>
          </div>
          <div className="overflow-auto flex-1 scrollbar-thin">
            <table className="w-full text-left text-[11px] table-fixed">
              <thead className="text-slate-500 font-black uppercase bg-slate-900 sticky top-0 z-10 border-b border-slate-800">
                <tr className="h-9">
                  <th className="px-5 w-[14%] uppercase">시간</th>
                  <th className="px-5 w-[8%] uppercase">담당자</th>
                  <th className="px-5 w-[14%] uppercase">거래처</th>
                  <th className="px-5 w-[10%] uppercase">변경항목</th>
                  <th className="px-5 w-[32%] uppercase">변경내용 (Before → After)</th>
                  <th className="px-5 w-[22%] uppercase">변경사유</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/40">
                {recentLogs.map((log) => {
                  const targetItem = forecastList.find(item => String(item.id) === String(log.target_id));
                  const isAmount = log.column_name.includes('planned') || log.column_name.includes('total') || log.column_name.includes('item');
                  const isDate = log.column_name.includes('date');
                  
                  // 🚨 숫자 변환 안전장치 추가
                  const oVal = isAmount ? (parseInt(String(log.old_value).replace(/[^0-9-]/g, '') || '0', 10)) : 0;
                  const nVal = isAmount ? (parseInt(String(log.new_value).replace(/[^0-9-]/g, '') || '0', 10)) : 0;
                  const diff = nVal - oVal;

                  return (
                    <tr key={log.id} className="hover:bg-slate-800/50 transition-colors h-10">
                      <td className="px-5 font-mono text-slate-500 text-[10px]">{log.changed_at}</td>
                      <td className="px-5 font-black text-slate-300">{log.editor_name}</td>
                      
                      <td className="px-5 text-slate-400 font-black truncate">
                        {(() => {if (targetItem) return targetItem.거래처;
                        const rawId = String(log.target_id);
                        const match = rawId.match(/\[(.*?)\]/);
                        if (match && match[1]) return match[1]; // 대괄호 안 한글만 추출
                        // return rawId.replace('forecast_', '').split(' ')[0]; // 대괄호 없으면 영어 꼬리표 삭제
                        })()}
                        </td>

                      <td className="px-5"><span className="text-blue-400 font-black tracking-tight">{getKoreanFieldName(log.column_name)}</span></td>
                      <td className="px-5">
                        <div className="flex items-center gap-2">
                          <span className="text-slate-600 line-through decoration-slate-700">{isAmount ? formatKRW(oVal) : log.old_value}</span>
                          <span className="text-slate-700 text-[9px]">→</span>
                          <span className={`font-black ${isAmount ? (diff > 0 ? 'text-red-400' : diff < 0 ? 'text-blue-400' : 'text-slate-200') : isDate ? 'text-yellow-400' : 'text-slate-200'}`}>
                            {isAmount ? formatKRW(nVal) : log.new_value}
                            {isAmount && diff !== 0 && (<span className="ml-1 text-[8px] font-black">{diff > 0 ? '▲' : '▼'}</span>)}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 text-slate-500 italic font-bold truncate" title={log.change_reason}>
                        {log.change_reason ? `💬 ${log.change_reason}` : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* 🟢 [Modal] 상세 이력 팝업 */}
      {selectedHistory !== null && (
        <div className="fixed inset-0 z-[99999] bg-black/60 backdrop-blur-sm flex items-center justify-center animate-fade-in">
          <div className="bg-white w-[500px] max-h-[80vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-scale-in">
            <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50 flex-none">
              <h3 className="font-black text-slate-800 flex items-center gap-2 text-sm"><History className="text-blue-600" size={18} /> 데이터 상세 변경 타임라인</h3>
              <button onClick={() => setSelectedHistory(null)} className="p-1 hover:bg-slate-200 rounded-full text-slate-400 transition-colors"><X size={18} /></button>
            </div>
            <div className="p-0 overflow-y-auto flex-1 bg-slate-50/30">
              {isHistoryLoading ? <div className="py-10 text-center text-slate-400 font-bold text-xs">데이터 로드 중...</div> : (
                <div className="flex flex-col divide-y divide-slate-100">
                  {selectedHistory.map((h, idx) => {
                    const diffInfo = getDiffInfo(h.column_name, h.old_value, h.new_value);
                    return (
                      <div key={idx} className="p-5 hover:bg-slate-50 transition-colors">
                        <div className="flex justify-between items-start mb-2 text-[10px]">
                           <span className="font-mono text-slate-400 font-bold">{h.changed_at}</span>
                           <span className="font-black text-slate-600 bg-slate-100 px-2 py-0.5 rounded border">{h.editor_name}</span>
                        </div>
                        <div className="text-xs font-black text-slate-800 mb-2 underline decoration-blue-200 underline-offset-4">{getKoreanFieldName(h.column_name)} 수정 사항</div>
                        <div className="flex flex-wrap items-center gap-2 text-[11px] font-mono bg-slate-50 p-3 rounded-xl border border-slate-100 shadow-inner">
                           <span className="text-slate-400 line-through">{formatKRW(h.old_value)}</span>
                           <span className="text-slate-300 text-[9px]">→</span>
                           <span className="text-slate-900 font-black">{formatKRW(h.new_value)}</span>
                           {diffInfo && <span className={`ml-auto px-2 py-0.5 rounded font-black text-[9px] border border-slate-100 ${diffInfo.color}`}>{diffInfo.text}</span>}
                        </div>
                        {h.change_reason && <div className="mt-3 text-[10px] text-slate-500 bg-yellow-50/50 p-2.5 rounded-lg border border-yellow-100/50 font-bold leading-relaxed">💬 {h.change_reason}</div>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLogBoard;
